import type { ReactNode } from "react"
import { useTableContext } from "./table.context"

export type TableRowActionsProps<TData = unknown> = {
  children(row: TData): ReactNode
}

/**
 * Registers a row-level actions column that renders at the end of each row.
 * Renders no DOM — it only configures the actions column via context.
 *
 * Must be placed before `Table.Content` in the children order so the
 * render function is available when cells mount.
 *
 * @example
 * ```tsx
 * <Table.RowActions<UserFragment>>
 *   {(user) => <UserActionMenu user={user} />}
 * </Table.RowActions>
 * ```
 */
function TableRowActions<TData = unknown>(props: TableRowActionsProps<TData>) {
  const { children } = props
  const { rowActionsRef } = useTableContext()

  rowActionsRef.current = {
    render: children as (row: unknown) => ReactNode,
  }

  return null
}

export default TableRowActions
